import { PAGE_PATH } from '@/constants';
import { fetchAreAllEligibleProductsInactiveOrSoldOut } from '@/services';
import {
  ApplicableType,
  DiscountType,
  FullDiscountPresentType,
  PromotionType,
  ShoppingCreditConditionType,
  _PromotionType,
  type AnyObj,
  type AnyObjOrNull,
} from '@/types';
import type { Common } from '@/types/global-business';
import NP from 'number-precision';
import { businessUtils } from '.';
import { commonUtils } from '../common';
import { useActivityStore, useCommodityStore } from './../../store';

type Config = {
  name: string | string[];
  description: string;
  startTime: string;
  endTime: string;
};

/**
 * 根据优惠类型获取优惠单位
 * @param discountType - 优惠类型
 * @returns
 */
const getMinimumByFullDiscountPresentType = (type: FullDiscountPresentType, num: number) => {
  return type !== FullDiscountPresentType.QuantityFullReduction
    ? `${NP.divide(num, 100)}元`
    : `${num}件`;
};

/**
 * 根据优惠类型获取优惠单位
 * @param discountType - 优惠类型
 * @returns
 */
const getUnitByDiscountType = (discountType: DiscountType) => {
  return discountType === DiscountType.FixedAmount
    ? '元'
    : discountType === DiscountType.FixedPercent
    ? '折'
    : '';
};

/**
 * 根据优惠类型获取优惠动作
 * @param discountType - 优惠类型
 * @returns
 */
const getActionByDiscountType = (discountType: DiscountType) => {
  return discountType === DiscountType.FixedAmount
    ? '减'
    : discountType === DiscountType.FixedPercent
    ? '打'
    : '';
};

/**
 * 根据优惠类型获取换算后优惠数值
 * @param discountType - 优惠类型
 * @param discount - 原优惠数值
 * @returns
 */
const getValueByDiscountType = (discountType: DiscountType, discount: number) => {
  return discountType === DiscountType.FixedAmount
    ? NP.divide(discount, 100)
    : discountType === DiscountType.FixedPercent
    ? NP.divide(discount, 10)
    : 0;
};

const discountActivityHandler = (config: Config, data: AnyObj) => {
  const n = data.minimum;
  // 5折和0折特殊处理
  if (data.discount === 50) {
    config.name = `第${n}件半价`;
    config.description = `每满${n}件，第${n}件享半价优惠`;
  } else if (data.discount === 0) {
    config.name = `第${n}件0元`;
    config.description = `每满${n}件，第${n}件享0元优惠`;
  } else {
    const d = NP.divide(data.discount, 10);
    config.name = `第${n}件${d}折`;
    config.description = `每满${n}件，第${n}件享${d}折优惠`;
  }

  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  return config;
};

const discountActivityLabelHandler = (data: AnyObj) => {
  const n = data.minimum;
  // 5折和0折特殊处理
  if (data.discount === 50) {
    return `第${n}件半价`;
  } else if (data.discount === 0) {
    return `第${n}件0元`;
  } else {
    const d = NP.divide(data.discount, 10);
    return `第${n}件${d}折`;
  }

};

const fullDiscountPresentHandler = (config: Config, data: AnyObj) => {
  // 默认标题满减
  config.name = `满减`;

  const computeHandler = (it: AnyObj) => {
    const discountType = it.discountValue.discountType;
    const discount = it.discountValue.discount;
    const value = getValueByDiscountType(discountType, discount);
    const unit = getUnitByDiscountType(discountType);
    const action = getActionByDiscountType(discountType);
    return { value, unit, action };
  };

  if (data.ruleType === 'cycle') {
    // 循环优惠
    const it = data.ruleValues[0];
    const result = computeHandler(it);

    let _description = '';

    // 0折特殊处理
    if (it.discountValue.discountType === DiscountType.noDiscount) {
      _description = `每满${getMinimumByFullDiscountPresentType(
        data.fullDiscountType,
        it.minimum
      )}送赠品`;

      // 标题显示满赠
      config.name = `满赠`;
    } else {
      _description = `每满${getMinimumByFullDiscountPresentType(
        data.fullDiscountType,
        it.minimum
      )}${result.action}${result.value}${result.unit}`;
    }

    config.description = _description;
  } else if (data.ruleType === 'ladder') {
    // 阶梯优惠
    data.ruleValues.forEach((it: AnyObj, index: number) => {
      const result = computeHandler(it);

      let _description = '';

      // 0折特殊处理
      if (it.discountValue.discountType === DiscountType.noDiscount) {
        _description = `满${getMinimumByFullDiscountPresentType(
          data.fullDiscountType,
          it.minimum
        )}送赠品`;

        // 标题显示满赠
        config.name = `满赠`;
      } else {
        _description = `满${getMinimumByFullDiscountPresentType(
          data.fullDiscountType,
          it.minimum
        )}${result.action}${result.value}${result.unit}`;
      }

      if (index === data.ruleValues.length - 1) {
        config.description += _description;
      } else {
        config.description += `${_description}，`;
      }
    });
  }

  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  // 实付满赠 标题 特殊处理
  if (data.fullDiscountType === FullDiscountPresentType.AmountFullPresent) {
    config.name = '实付满赠';
  }

  return config;
};

const fullDiscountPresentLabelHandler = (data: AnyObj, price: number) => {
  const commodityStore = useCommodityStore();
  let text = commodityStore.isVipProduct ? "年卡 " : "";

  const computeHandler = (it: AnyObj) => {
    const discountType = it.discountValue.discountType;
    const discount = it.discountValue.discount;
    const value = getValueByDiscountType(discountType, discount);
    const unit = getUnitByDiscountType(discountType);
    const action = getActionByDiscountType(discountType);
    return { value, unit, action };
  };

  if (data.ruleType === 'cycle') {
    // 循环优惠
    const it = data.ruleValues[0];
    const result = computeHandler(it);

    // 0折特殊处理
    if (it.discountValue.discountType === DiscountType.noDiscount) {
      return ""
    } else {
      if (data.fullDiscountType !== FullDiscountPresentType.QuantityFullReduction) {
        if (it.discountValue.discountType === DiscountType.FixedAmount) {
          return text + `每满${getMinimumByFullDiscountPresentType(
            data.fullDiscountType,
            it.minimum
          )}减${result.value}元`;
        } else {
          return text + `每满${getMinimumByFullDiscountPresentType(
            data.fullDiscountType,
            it.minimum
          )}预计单件${businessUtils.price.roundedUp(NP.divide(price, 100) * NP.divide(result.value, 10))}元起`;
        }
      } else {
        if (it.discountValue.discountType === DiscountType.FixedAmount) {
          return text + `每买${it.minimum}件预计单件${businessUtils.price.roundedUp(NP.divide((NP.times(NP.divide(price, 100), it.minimum) - result.value), it.minimum))}元起`
        } else {
          return text + `每买${it.minimum}件预计单件${businessUtils.price.roundedUp(NP.divide((NP.times(NP.divide(price, 100), it.minimum) * NP.divide(result.value, 10)), it.minimum))}元起`
        }
      }
    }
  } else if (data.ruleType === 'ladder') {
    // 阶梯优惠

    const it = data.ruleValues[0];
    const result = computeHandler(it);

    // 0折特殊处理
    if (it.discountValue.discountType === DiscountType.noDiscount) {
      return ""
    } else {
      if (data.fullDiscountType !== FullDiscountPresentType.QuantityFullReduction) {
        if (it.discountValue.discountType === DiscountType.FixedAmount) {
          return text + `满${getMinimumByFullDiscountPresentType(
            data.fullDiscountType,
            it.minimum
          )}减${result.value}元`;
        } else {
          return text + `满${getMinimumByFullDiscountPresentType(
            data.fullDiscountType,
            it.minimum
          )}预计单件${businessUtils.price.roundedUp(NP.divide(price, 100) * NP.divide(result.value, 10))}元起`;
        }
      } else {
        if (it.discountValue.discountType === DiscountType.FixedAmount) {
          return text + `买${it.minimum}件预计单件${businessUtils.price.roundedUp(NP.divide((NP.times(NP.divide(price, 100), it.minimum) - result.value), it.minimum))}元起`
        } else {
          return text + `买${it.minimum}件预计单件${businessUtils.price.roundedUp(NP.divide((NP.times(NP.divide(price, 100), it.minimum) * NP.divide(result.value, 10)), it.minimum))}元起`
        }
      }
    }
  }

  return ""
};

const selectiveGiftActivityHandler = (config: Config, data: AnyObj) => {
  config.name = `任选满赠`;

  if (data.ruleType === 'cycle') {
    // 循环优惠
    const it = data.ruleValues[0];
    config.description = `每满${it.minimum}件送赠品`;
  } else if (data.ruleType === 'ladder') {
    // 阶梯优惠
    data.ruleValues.forEach((it: AnyObj, index: number) => {
      const _description = `满${it.minimum}件送赠品`;
      if (index === data.ruleValues.length - 1) {
        config.description += _description;
      } else {
        config.description += `${_description}，`;
      }
    });
  }

  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  return config;
};

const shoppingCreditsDeductionHandler = (config: Config, data: AnyObj) => {
  config.name = data.name;
  config.description =
    data.minimum === 0
      ? `购物金最高抵扣${data.deductionRate}%`
      : `满${NP.divide(data.minimum, 100)}元可抵扣${data.deductionRate}%`;
  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  return config;
};

const shoppingCreditsDeductionLabelHandler = (data: AnyObj, price: number) => {
  return `购物金可抵扣${NP.times(NP.divide(price, 100), NP.divide(data.deductionRate, 100))}元`
};

const shoppingCreditsClaimHandler = (config: Config, data: AnyObj) => {
  config.name = data.name;

  // 兼容服务端字段类型错误问题
  data.ruleValues = data.shoppingCreditsClaimActivityRuleValues;

  const devide = (num: number) => NP.divide(num, 100);

  if (data.ruleType === 'cycle') {
    // 循环优惠
    const it = data.ruleValues[0];

    // 判断购物金条件类型
    if (data.shoppingCreditConditionType === ShoppingCreditConditionType.quantityFullReduction) {
      // 满件数
      // 固定百分比
      if (it.discountValue.discountType === DiscountType.FixedPercent) {
        config.description = `每满${it.minimum}件可返商品实付金额${
          it.discountValue.discount
        }%的购物金，最高可获得${devide(it.maximumOffer)}元购物金`;
      } else {
        // 固定金额
        config.description = `每满${it.minimum}件可获得${devide(
          it.discountValue.discount
        )}元购物金，最高可获得${devide(it.maximumOffer)}元购物金`;
      }
    } else {
      // 满金额
      // 固定百分比
      if (it.discountValue.discountType === DiscountType.FixedPercent) {
        config.description = `每满${devide(it.minimum)}元可返商品实付金额${
          it.discountValue.discount
        }%的购物金，最高可获得${devide(it.maximumOffer)}元购物金`;
      } else {
        // 固定金额
        config.description = `每满${devide(it.minimum)}元可获得${devide(
          it.discountValue.discount
        )}元购物金，最高可获得${devide(it.maximumOffer)}元购物金`;
      }
    }
  } else if (data.ruleType === 'ladder') {
    // 阶梯优惠
    data.ruleValues.forEach((it: AnyObj, index: number) => {
      let _description = '';

      if (it.minimum === 0) {
        _description = `购物金最高抵扣${devide(it.discountValue.discount)}%`;
      } else {
        // 判断购物金条件类型
        if (
          data.shoppingCreditConditionType === ShoppingCreditConditionType.quantityFullReduction
        ) {
          // 满件数
          // 固定百分比
          if (it.discountValue.discountType === DiscountType.FixedPercent) {
            _description = `买满${it.minimum}件,可返商品实付金额的${it.discountValue.discount}%购物金`;
          } else {
            // 固定金额
            _description = `满${it.minimum}件可获得${devide(it.discountValue.discount)}元购物金`;
          }
        } else {
          // 满金额
          // 固定百分比
          if (it.discountValue.discountType === DiscountType.FixedPercent) {
            _description = `买满${devide(it.minimum)}元,可返商品实付金额的${
              it.discountValue.discount
            }%购物金`;
          } else {
            // 固定金额
            _description = `满${devide(it.minimum)}元可获得${devide(
              it.discountValue.discount
            )}元购物金`;
          }
        }
      }

      if (index === data.ruleValues.length - 1) {
        config.description += _description;
      } else {
        config.description += `${_description}，`;
      }
    });
  }

  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  return config;
};

const purchasePremiumHandler = (config: Config, data: AnyObj) => {
  config.name = `换购`;
  config.description = `满${NP.divide(data.minimum, 100)}元享优惠换购`;
  config.startTime = commonUtils.date.format(data.validityPeriod.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.validityPeriod.endTime, 'YYYY.MM.DD');

  return config;
};

const packageDiscountHandler = (config: Config, data: AnyObj) => {
  config.name = '打包一口价';
  config.description = data?.activityContentType[0];
  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  return config;
};

export default {
  /**
   * 获取优惠活动配置信息
   * @param item
   * @returns
   */
  getConfig: (item: AnyObjOrNull) => {
    const config: Config = {
      name: '',
      description: '',
      startTime: '',
      endTime: '',
    };

    if (!item) return config;

    const actions: Common.RespondAction<_PromotionType, Config> = {
      // 活动 => eg:第二件半价
      [_PromotionType.DiscountActivity]: (conf) => discountActivityHandler(conf, item),

      // 活动 => 满减送
      [_PromotionType.FullDiscountPresent]: (conf) => fullDiscountPresentHandler(conf, item),

      // 加价购
      [_PromotionType.PurchasePremium]: (conf) => purchasePremiumHandler(conf, item),

      // 打包一口价
      [_PromotionType.PackageDiscount]: (conf) => packageDiscountHandler(conf, item),

      // 任选满赠
      [_PromotionType.SelectiveGiftActivity]: (conf) => selectiveGiftActivityHandler(conf, item),

      // 购物金获得
      [_PromotionType.ShoppingCreditsClaimActivity]: (conf) =>
        shoppingCreditsClaimHandler(conf, item),

      // 购物金抵扣
      [_PromotionType.ShoppingCreditsDeductionActivity]: (conf) =>
        shoppingCreditsDeductionHandler(conf, item),
    };

    actions[item.__typename as _PromotionType]?.(config);

    return config;
  },
  /**
   * 获取优惠活动明细配置标签
   * @param item
   * @returns
   */
  getLabelConfig: (item: AnyObjOrNull, price: number) => {
    if (!item) return "";

    const actions: Common.RespondAction<_PromotionType, string> = {
      // 活动 => eg:第二件半价
      [_PromotionType.DiscountActivity]: () => discountActivityLabelHandler(item),

      // 活动 => 满减送
      [_PromotionType.FullDiscountPresent]: () => fullDiscountPresentLabelHandler(item, price),

      // 加价购
      [_PromotionType.PurchasePremium]: () => "",

      // 打包一口价
      [_PromotionType.PackageDiscount]: () => "",

      // 任选满赠
      [_PromotionType.SelectiveGiftActivity]: () => "",

      // 购物金获得
      [_PromotionType.ShoppingCreditsClaimActivity]: () => "",

      // 购物金抵扣
      [_PromotionType.ShoppingCreditsDeductionActivity]: () => shoppingCreditsDeductionLabelHandler(item, price),
    };

    return actions[item.__typename as _PromotionType]?.();
  },
  /**
   * 获取活动标签渲染数据
   * @param data
   * @returns
   */
  getParticipatingActivities: (data: AnyObject) => {
    const pa = data?.participatingActivities ?? [];
    if (!pa.length) return [];

    return pa.map((it: AnyObject) => {
      return { name: it.customFields.activityName };
    });
  },
  /**
   * 获取适用的商品
   * @param type - 适用类型
   * @param ids - 商品id集合
   * @returns
   */
  getApplicableProducts: (type: ApplicableType, ids: string[], take = 50) => {
    const activityStore = useActivityStore();

    const actions: Common.RespondAction<ApplicableType> = {
      [ApplicableType.all]: () => activityStore.getSuitCommodityList(undefined, [], take),
      [ApplicableType.availableGoods]: () => activityStore.getSuitCommodityList(true, ids, take),
      [ApplicableType.unusableGoods]: () => activityStore.getSuitCommodityList(false, ids, take),
    };

    return actions[type]?.();
  },
  /**
   * 活动是否异常
   * @param id - 活动id
   */
  isAbnormal: async (id: string) => {
    try {
      if (!id) {
        console.log(`活动id错误`);
        return;
      }

      const pages = getCurrentPages();
      const currentPage = pages[pages.length - 1];

      const types = {
        [PAGE_PATH.fulfilPresent.route]: PromotionType.fullDiscountPresent,
        [PAGE_PATH.couponSuit.route]: PromotionType.coupon,
        [PAGE_PATH.halfDiscount.route]: PromotionType.discountActivity,
        [PAGE_PATH.purchasePremium.route]: PromotionType.purchaseAtAPremium,
        [PAGE_PATH.packageDiscount.route]: PromotionType.packageDiscount,
        [PAGE_PATH.selectiveGift.route]: PromotionType.selectiveGift,
        [PAGE_PATH.shoppingCreditsClaim.route]: PromotionType.shoppingCreditsClaim,
        [PAGE_PATH.shoppingCreditsDeduction.route]: PromotionType.shoppingCreditsDeduction,
      };

      const targetType = types[`/${currentPage.route}`];

      if (!targetType) {
        console.log(`类型错误，请检查`, {
          types,
          targetType,
          route: currentPage.route,
        });
      }

      const res = await fetchAreAllEligibleProductsInactiveOrSoldOut(id, targetType);
      if (res) {
        businessUtils.modal.showTip('活动商品已失效');
        return res;
      }
    } catch (error) {
      console.log(error);
    }
  },
};
